<template>
  <div class="mb-4 bg-white mt-3">
    <MDBNavbar class="d-flex p-0">
      <MDBNavbarNav :class="`${menuOpen ? 'col-9' : 'col-3'} col-lg-3 d-flex flex-row`">
        <MDBNavbarItem linkClass="py-0 p-relative">
          <MDBNavbarToggler target="#navbarSupportedContent" @click="toggleMenu" />
          <div v-if="menuOpen" class="menu-wrap-bg" @click="toggleMenu"></div>
          <div v-if="menuOpen" @click="toggleMenu" class="menu-wrap">
            <template v-if="
              !(
                accessData &&
                accessData._restricted_access &&
                !accessData._client_type_approved_at
              )
            ">
              <MDBNavbarNav v-for="(item, id) in menuLinks" :key="`menu_${id}`" class="header_menu">
                <h4 class="fw-bold mb-0 font-25">
                  <router-link class="text-black" :to="menuItemTo(item)">
                    <div v-html="$t(`global.menu.${item.name}`)"></div>
                  </router-link>
                </h4>
              </MDBNavbarNav>
            </template>
            <MDBNavbarNav class="header_menu">
              <h4 class="fw-bold mb-0 text-black cursor--pointer font-25" @click="logout()">
                {{ $t("global.menu.logOut") }}
              </h4>
            </MDBNavbarNav>
          </div>
        </MDBNavbarItem>
        <MDBNavbarItem :class="`${menuOpen ? '' : 'd-none'} d-lg-block`">
          <router-link :to="{ name: 'dashboard' }" @click="() => (menuOpen = false)">
            <img class="menu-logo" :src="require('@/assets/img/logo.svg')" alt="logo" />
          </router-link>
        </MDBNavbarItem>
      </MDBNavbarNav>
      <MDBNavbarNav :class="`justify-content-center w-100 col flex-row d-lg-flex ${menuOpen ? 'd-none' : 'd-flex'
        }`">
        <MDBNavbarItem class="w-100 text-center font-14 font-md-16 font-lg-18 fw-bold">
          <span v-if="!loading" v-html="displayTitle || $t('global.nav.' + currentRoute)"></span>
        </MDBNavbarItem>
      </MDBNavbarNav>
      <MDBNavbarNav class="right-menu col-3 d-flex flex-row justify-content-end align-items-center pe-lg-3 pe-1">
        <LanguageSwitcher />
        <MDBNavbarItem>
          <router-link :to="{ name: 'mailbox' }" class="nav-link px-lg-3 px-1 px-sm-2 py-0">
            <img :src="require('@/assets/img/icons/envelope.svg')" alt="envelope" />
            <MDBBadge v-if="messages > 0" color="danger" pill notification>
              {{ messages }}
            </MDBBadge>
          </router-link>
        </MDBNavbarItem>
        <FavoritePreviewNavItem v-if="context.canSeeFavProducts" />

        <CartPreviewNavItem v-if="context.canSeeCartIcon" />

        <MDBNavbarItem v-if="context.canSeeHelpWithLinkTooltip" class="menu-item-rounded p-relative" linkClass="py-0">
          <MDBTooltip v-model="tooltip1" tag="a" direction="left" v-if="tenantConfig?.showToolTip">
            <template #reference>
              <img :src="require('@/assets/img/icons/question-mark.svg')" alt="help" class="cursor--pointer"
                @click="redirectToContact()" />
            </template>
            <template #tip>
              <div class="tooltip-min-width" v-html="$t('global.tooltips.info')"></div>
            </template>
          </MDBTooltip>
          <img :src="require('@/assets/img/icons/question-mark.svg')" alt="help" class="cursor--pointer"
            @click="redirectToContact()" v-else />
        </MDBNavbarItem>
        <MDBNavbarItem v-if="context.canSeeHelpTooltip" class="menu-item-rounded p-relative" linkClass="py-0">
          <MDBTooltip v-model="tooltip1" tag="a" direction="left" v-if="tenantConfig?.showToolTip">
            <template #reference>
              <img :src="require('@/assets/img/icons/question-mark.svg')" alt="help" class="cursor--pointer" />
            </template>
            <template #tip>
              <div class="tooltip-min-width" v-html="$t('global.tooltips.info')"></div>
            </template>
          </MDBTooltip>
          <img :src="require('@/assets/img/icons/question-mark.svg')" alt="help" class="cursor--pointer" v-else />
        </MDBNavbarItem>
      </MDBNavbarNav>
    </MDBNavbar>
  </div>
</template>

<script setup>
import {
  MDBNavbar,
  MDBNavbarNav,
  MDBNavbarToggler,
  MDBNavbarItem,
  MDBTooltip,
  MDBBadge,
} from "mdb-vue-ui-kit";
import { useRoute, useRouter } from "vue-router";
import { computed, ref, watch, defineProps, onMounted } from "vue";
import { useStore } from "vuex";
import i18n from "@/plugins/i18n";
import LanguageSwitcher from "@/components/LanguageSwitcher.vue";
import CartPreviewNavItem from "@/components/Layout/CartPreviewNavItem.vue";
import FavoritePreviewNavItem from "@/components/Layout/FavoritePreviewNavItem.vue";
import { useItemsConditions } from '@/composables/useItemsConditions'
import { useTenantConfig } from "@/composables/useTenantConfig";
import { getHelpLink } from "@/composables/useProfileFieldCheck";

const store = useStore();
const route = useRoute();
const router = useRouter();
const storedLocale = computed(() => store.getters.getCurrentLocale);
const accessData = computed(() => store.getters.getAccessData);

const { tenantConfig } = useTenantConfig();
const { showHelpLink } = getHelpLink(tenantConfig);

const menuOpen = ref(false);
const tooltip1 = ref(false);

// Destructure specific context access flags from useItemsConditions
// This provides direct access to individual permission checks
// such as feature toggles or tenant-based availability (e.g. my-bs-app)
const { getContext } = useItemsConditions()
const context = computed(() => getContext())

const me = computed(() => store.getters.getMe);
const messages = computed(() => {
  return store.getters.getUnreadMessages;
});
const currentRoute = computed(() => {
  return route.name;
});

const props = defineProps({
  title: String,
});

const toggleMenu = () => (menuOpen.value = !menuOpen.value);
const textPage = computed(() => store.getters.getTextPage);
const training = computed(() => store.getters.getTraining);
const hunter = computed(() => store.getters.getSingleHunter);
const mediaCategory = computed(() => store.getters.getSingleMediaCategory);
const files = computed(() => store.getters.getSinglePriceList);
const fetchedMenu = computed(() => store.getters.getFetchMenu);
const loading = computed(() => store.getters.getPending);

const menuLinks = computed(() => {
  return store.getters.getMenu;
});

const menuItemTo = (item) => {
  const filters = typeof item.link === "string" ? item.link.trim() : "";

  if (!filters) {
    return { name: item.name };
  }

  return { name: item.name, query: { filters } };
};

const displayTitle = computed(() => {
  const routeName = currentRoute.value;
  const locale = storedLocale.value;

  const titleResolvers = [
    () => props.title,
    () => routeName === "text_page" && textPage.value?.title?.[locale],
    () => routeName === "hunter" && hunter.value && `${i18n.global.t("pageName.hunterShop")} ${hunter.value.title}`,
    () => routeName === "single_media" && mediaCategory.value?.name,
    () => routeName === "pkw" && files.value && i18n.global.t(`productList.${route.params.slug}.title`),
    () => routeName === "training" && Object.keys(training.value).length > 0 && training.value.title?.[locale],
  ];

  return titleResolvers.map(resolver => resolver()).find(Boolean) || false;
});

const isWholesaler = ref(false);
const isWholesalerStaff = ref(false);
const isStaff = ref(false);

watch(me, async (newVal) => {
  if (newVal && newVal.roles.find((item) => item.name == "wholesaler")) {
    isWholesaler.value = true;
  }
  if (newVal && newVal.roles.find((item) => item.name == "wholesalerStaff")) {
    isWholesalerStaff.value = true;
  }
  if (newVal && newVal.roles.find((item) => item.name == "staff")) {
    isStaff.value = true;
  }
});

const logout = async () => {
  await store.dispatch("logout");
};

const redirectToContact = () => {
  const link = showHelpLink();
  const routeName = link === "contact" ? "contact_service" : link;
  const targetRoute =
    (routeName && router.hasRoute(routeName) && routeName) ||
    (router.hasRoute("contact_service") && "contact_service") ||
    (router.hasRoute("service_number") && "service_number");

  if (targetRoute) {
    router.push({ name: targetRoute });
  }
};

onMounted(async () => {
  if (!fetchedMenu.value) {
    store.dispatch("checkMenuFetched");
  }

  await store.dispatch("loadUserSlides");
});
</script>

<style scoped lang="scss">
@import "@/styles/menu.scss";
</style>
